import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import usePageMeta from "../hooks/usePageMeta";

export default function Partner() {
  usePageMeta("Gym Pickup Dashboard | Tiff. Wellness Co.", "Gym partner pickup verification.", { robots: "noindex,nofollow" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopScan = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setScanning(false);
  };

  const startScan = async () => {
    if (!("BarcodeDetector" in window)) {
      toast.info("QR scanning isn't supported in this browser — enter the code manually.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setScanning(true);
      setTimeout(async () => {
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        const tick = async () => {
          if (!streamRef.current || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) {
              const value = (codes[0].rawValue || "").trim().toUpperCase();
              stopScan();
              setCode(value);
              toast.success(`Scanned ${value} — verifying…`);
              await verifyCode(value);
              return;
            }
          } catch {}
          requestAnimationFrame(tick);
        };
        tick();
      }, 100);
    } catch {
      toast.error("Camera access denied — enter the code manually.");
      stopScan();
    }
  };

  useEffect(() => () => stopScan(), []);

  const load = () => api.get("/partner/pickups").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    if (user === false) navigate("/auth?next=/partner");
    if (user && user !== false && !["partner", "admin"].includes(user.role)) navigate("/account");
    if (user && ["partner", "admin"].includes(user.role)) load();
  }, [user]); // eslint-disable-line

  const verifyCode = async (value) => {
    setBusy(true); setResult(null);
    try {
      const { data: d } = await api.post("/partner/verify", { code: value });
      setResult({ ok: true, ...d });
      toast.success(`Order ${d.order_number} marked collected`);
      setCode(""); load();
    } catch (err) { setResult({ ok: false, msg: errMsg(err) }); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    await verifyCode(code);
  };

  if (!user || user === false || !data) return <div className="py-20 text-center text-sm">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-4xl mb-2">Partner pickups</h1>
      <p className="text-sm text-[#171715]/70 mb-8">Assigned locations only. Customer details are limited to what's needed for handoff.</p>

      <form onSubmit={verify} className="card-paper p-5 mb-8">
        <label htmlFor="verify-code" className="text-sm font-medium block mb-2">Verify pickup code</label>
        <div className="flex gap-2">
          <input id="verify-code" data-testid="partner-verify-pickup-input" className="input-line font-mono uppercase" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. 7KP2QX" />
          <button data-testid="partner-verify-pickup-button" className="btn-ink" disabled={busy || code.length < 4}>{busy ? "…" : "Verify"}</button>
          <button type="button" data-testid="partner-scan-qr-button" className="btn-ghost" onClick={scanning ? stopScan : startScan}>{scanning ? "Stop" : "Scan QR"}</button>
        </div>
        {scanning && (
          <div className="mt-3">
            <video ref={videoRef} data-testid="partner-scan-video" className="w-full max-w-xs border border-[#EBD9D0]" muted playsInline />
            <p className="text-xs text-[#171715]/55 mt-1">Point the camera at the customer's pickup QR. The QR contains only the code — no personal data.</p>
          </div>
        )}
        {result && (
          <p data-testid="partner-verify-result" className={`text-sm mt-3 ${result.ok ? "text-[#A35847]" : "text-[#9c3b2a]"}`}>
            {result.ok ? `✓ ${result.order_number} — ${result.bottle_count} bottle(s): ${result.items.map((i) => `${i.qty}× ${i.name}`).join(", ")} — marked collected (one-time; repeat scans are rejected)` : result.msg}
          </p>
        )}
      </form>

      <h2 className="text-2xl mb-4">Upcoming pickups</h2>
      <div className="space-y-3" data-testid="partner-pickup-list">
        {data.pickups.map((p) => (
          <div key={p.id} className="card-paper p-4 text-sm flex flex-wrap justify-between gap-2" data-testid={`partner-pickup-${p.order_number}`}>
            <div>
              <p className="font-medium">{p.order_number} · {p.customer_first_name}</p>
              <p className="text-[#171715]/70">{p.items.map((i) => `${i.qty}× ${i.name}`).join(", ")} ({p.bottle_count} bottles)</p>
            </div>
            <div className="text-right">
              <p>{p.pickup.date} · {p.pickup.window_label}</p>
              <p className="font-mono uppercase text-xs text-[#171715]/60">{p.fulfillment_status}</p>
            </div>
          </div>
        ))}
        {data.pickups.length === 0 && <p className="text-sm text-[#171715]/60 card-paper p-4">No upcoming pickups for your locations.</p>}
      </div>
    </div>
  );
}
